import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import EANSearch from "./eanSearch";
import ReportListItem from "./reportListItem";
import { HttpRequestStatus, LoadingContainer } from "./loadingContainer";
import useReportRows from "../hooks/useReportRows";
import { CreateReportRowPayload } from "../api/models/reportRow.models";
import { DialogInputs } from "../hooks/useMaterials";
import { v4 as uuidv4 } from "uuid";
import {
  CreateReportPayload,
  Report,
  ReportStatus,
  ReportType,
} from "../api/models/report.models";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { Family, FullProductData } from "../api/models/fullProductData.models";
import { useReportFormContext } from "../contexts/reportFormContext";
import { AkeneoModel } from "../api/models/akeneoModels.models";
import useEcomodClasses from "../hooks/useEcomodClasses";
import useSupClasses from "../hooks/useSupClasses";
import useReport from "../hooks/useReport";
import { useNavigate } from "react-router";
import useCombineModelValues from "../hooks/useCombineModelValues";
import {
  convertAkeneoRowsToEcomodRows,
  convertEcomodRowsToAkeneoRows,
} from "../utils/rowUtils";
import { RowValuesWithEcomodClass } from "../api/models/rowValues.models";
import { CreateProductPayload } from "../api/models/product.models";
import { CreatePackageRowPayload } from "../api/models/packageRow.models";
import CreateProductView from "../views/createProductView";
import useSearchProductsWithEan from "../hooks/useSearchWithEan";
import { FullModel } from "../api/models/fullModel.models";
import EditReportRowView from "../views/editReportRowView";
import FileUploadDialog from "../dialogs/fileUploadDialog";
import { useReportingCycleContext } from "../contexts/reportingCycleContext";
import { ReportingCycleEnum } from "../api/models/reportingCycle.models";
import CreateMaterialView from "./createMaterialView";

interface ReportFormProps {
  reportId?: number;
  reportData?: Report;
  isLoadingReport: boolean;
}

const ReportForm: React.FC<ReportFormProps> = ({
  reportId,
  reportData,
  isLoadingReport,
}: ReportFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [
    toast,
    companyId,
    organizationId,
    year,
    quarter,
    month,
    setYear,
    setQuarter,
    setMonth,
  ] = useUIStore(
    useShallow((state: UIState) => [
      state.toastRef,
      state.companyId,
      state.organizationId,
      state.selectedYearForNewReport,
      state.selectedQuarterForNewReport,
      state.selectedMonthForNewReport,
      state.setSelectedYearForNewReport,
      state.setSelectedQuarterForNewReport,
      state.setSelectedMonthForNewReport,
    ]),
  );

  const [showImportExcelDialog, setShowImportExcelDialog] =
    React.useState<boolean>(false);

  const [synkkaData, setSynkkaData] = React.useState<
    FullProductData | FullModel | undefined
  >(undefined);

  // increments on every search submit so repeat searches for the same EAN still retrigger row creation
  const [searchToken, setSearchToken] = React.useState(0);

  const { createNewReport, isCreatingNewReport } = useReport({
    reportId: undefined,
    disableSearch: true,
  });

  const { reportingCycle } = useReportingCycleContext();

  const {
    productData,
    searchEan,
    setSearchEan,
    eanSearchData,
    eanSearchLoading,
  } = useSearchProductsWithEan({ companyId: companyId as number });

  const { getCombinedValues } = useCombineModelValues();

  useEffect(() => {
    if (Array.isArray(productData?.data) && productData.data.length === 0) {
      console.error("EAN not found in Akeneo:", searchEan);
      setShowCreateProductView(true);
    }
  }, [productData]);

  const {
    createProductAndReportRow,
    createReportRowData,
    updateReportRowData,
    deleteReportRowData,
    isCreatingReportRow,
    isUpdatingReportRow,
  } = useReportRows();

  const { ecomodClasses, isLoading: isLoadingEcomodClasses } =
    useEcomodClasses();

  const { supClasses, isLoading: isLoadingSupClasses } = useSupClasses();

  const {
    setIsUpdatingReportRow,
    showCreateProductView,
    setShowCreateProductView,
    showEditReportRowView,
    setShowEditReportRowView,
    showCreateMaterialView,
    setShowCreateMaterialView,
  } = useReportFormContext();

  useEffect(() => {
    if (isCreatingReportRow || isUpdatingReportRow) {
      setIsUpdatingReportRow(true);
    } else {
      setIsUpdatingReportRow(false);
    }
  }, [isCreatingReportRow, isUpdatingReportRow]);

  useEffect(() => {
    if (eanSearchData?.data && eanSearchData?.data?.length > 0) {
      if (eanSearchData?.isSynkkaData) {
        // showManualProductView is used to create a new product from Synkka data
        setSynkkaData(eanSearchData.data[0]);
        setShowCreateProductView(true);
      } else {
        createRowFromProductData(
          eanSearchData.data[0],
          eanSearchData.synkkaFamily,
        );
      }
    }

    if (eanSearchData?.data && eanSearchData?.data?.length === 0) {
      setShowCreateProductView(true);
    }
  }, [eanSearchData, searchToken]);

  const handleReportCreate = async () => {
    if (!year) {
      toast?.current?.show({
        severity: "error",
        detail: t("error-no-year-selected-detail"),
        life: 5000,
      });
      return;
    }

    const createReportPayload: CreateReportPayload = {
      companyId: companyId as number,
      organizationId: organizationId ? organizationId : undefined,
      year: year ? year : 2025,
      quarter:
        reportingCycle === ReportingCycleEnum.QUARTERLY
          ? quarter
            ? quarter
            : 1
          : undefined,
      month:
        reportingCycle === ReportingCycleEnum.MONTHLY
          ? month
            ? month
            : 1
          : undefined,
      reportStatus: ReportStatus.Draft,
      reportType: ReportType.EAN,
      rows: [],
    };
    const result = await createNewReport(createReportPayload, undefined);

    if (result) {
      // reset the selected year, quarter, month in the store
      setYear(null);
      setQuarter(null);
      setMonth(null);
    }

    return result;
  };

  const createRowFromProductData = async (
    product: FullProductData | FullModel,
    family: Family,
  ) => {
    let currentReportId = reportId;

    try {
      // if no reportId is provided, create a new report
      if (!currentReportId) {
        if (!year && !quarter && !month) {
          toast?.current?.show({
            severity: "error",
            detail: "Year, quarter, or month must be defined for new report!",
          });
          throw new Error(
            "Year, quarter, or month must be defined for new report!",
          );
        }

        const result = await handleReportCreate();
        if (result) {
          currentReportId = result.id;
        }
      }

      const productEan =
        family === Family.product
          ? (product as FullProductData).product.ean ||
            (product as FullProductData).product.internalReference
          : (product as FullModel).model.ean ||
            (product as FullModel).model.internalReference;

      const productName =
        family === Family.product
          ? (product as FullProductData).product.name
          : (product as FullModel).model.name;

      const productNumber =
        family === Family.product
          ? (product as FullProductData).product.internalReference
          : (product as FullModel).model.internalReference;

      const responsibleToReport =
        family === Family.product
          ? (product as FullProductData).product.responsibleToReport
          : (product as FullModel).model.responsibleToReport;

      // Duplicate EANs are allowed, but let the user know rather than blocking them
      /* const existingEanCount =
        reportData?.rows?.filter((row) => row.ean === productEan).length ?? 0;
      if (existingEanCount > 0) {
        toast?.current?.show({
          severity: "info",
          detail: t("label-product-already-added-info", {
            count: existingEanCount,
          }),
          life: 3000,
        });
      } */

      let rowValues: RowValuesWithEcomodClass[] = [];

      // A model has either a package association or its own packageRows, never both, so
      // only hit the recursive combine endpoint when it's associated with a package.
      const modelPackageAssociations =
        family === Family.product_model
          ? (product as FullModel).model.packageAssociations
          : null;

      if (
        modelPackageAssociations &&
        Object.keys(modelPackageAssociations).length > 0
      ) {
        const combinedResult = await getCombinedValues(
          (companyId as number).toString(),
          (product as FullModel).model.identifier,
        );

        rowValues = convertAkeneoRowsToEcomodRows(
          combinedResult?.rowValues || [],
          ecomodClasses || [],
          supClasses || [],
        );
      } else {
        rowValues = product?.packageRows.rowValues
          ? convertAkeneoRowsToEcomodRows(
              product?.packageRows.rowValues,
              ecomodClasses || [],
              supClasses || [],
            )
          : [];
      }

      const payload: CreateReportRowPayload = {
        reportId: currentReportId as number,
        name: productName as string,
        ean: productEan as string,
        internalReference: productNumber || "",
        amount: 0,
        active: responsibleToReport !== undefined ? responsibleToReport : true,
        values: rowValues,
      };

      await createReportRowData(payload);

      if (!reportId && currentReportId) {
        navigate(`/report/${currentReportId}`);
      }
    } finally {
      // always reset the search/view state, even on failure, so retrying the same EAN works
      handleCloseView();
      setSynkkaData(undefined);
    }
  };

  const handleCloseView = () => {
    setShowCreateProductView(false);
    setSynkkaData(undefined);
    setSearchEan("");
  };

  const handleSearchProduct = (ean: string) => {
    setSearchEan(ean);
    setSearchToken((token) => token + 1);
  };

  const handleRemoveReport = (rowId: number) => {
    deleteReportRowData(rowId);
    return;
  };

  const createRowPayload = (data: DialogInputs) => {
    const payload: CreateReportRowPayload = {
      ...data,
      amount: 0,
      active:
        data.responsibleToReport !== undefined
          ? data.responsibleToReport
          : true,
      ean: data.ean,
    };
    return payload;
  };

  const handleCreateNewRowFromModel = async ({
    name,
    ean,
    model,
  }: {
    name: string;
    ean: string;
    model: AkeneoModel;
  }) => {
    // if no reportId is provided, create a new report
    let currentReportId = reportId;
    if (!currentReportId) {
      const result = await handleReportCreate();
      if (result) {
        currentReportId = result.id;
      }
    }

    const combinedResult = await getCombinedValues(
      (companyId as number).toString(),
      model.identifier,
    );

    const rowValues = convertAkeneoRowsToEcomodRows(
      combinedResult?.rowValues || [],
      ecomodClasses || [],
      supClasses || [],
    );

    const payload: CreateReportRowPayload = {
      reportId: currentReportId as number,
      name: model.name,
      ean: model.ean as string,
      amount: 0,
      active: model.responsibleToReport || true,
      values: rowValues,
    };

    await createReportRowData(payload);

    handleCloseView();
    setSynkkaData(undefined);
    if (!reportId && currentReportId) {
      navigate(`/report/${currentReportId}`);
    }
  };

  const handleCreateNewRow = async (data: DialogInputs) => {
    let currentReportId = reportId;

    // if no reportId is provided, create a new report
    if (!currentReportId) {
      const result = await handleReportCreate();
      currentReportId = result?.id;

      if (!currentReportId) {
        return;
      }
    }

    const packageRowIdentifier = uuidv4();

    const createPackageRowPayload: CreatePackageRowPayload = {
      identifier: packageRowIdentifier,
      name: data.name,
      synkkaImported: false,
      companyId: (companyId as number).toString(),
      family: Family.package_rows,
      rowValues: convertEcomodRowsToAkeneoRows(data.values || []),
    };

    const createProductPayload: CreateProductPayload = {
      identifier: uuidv4(),
      name: data.name,
      ean: data.ean,
      companyId: (companyId as number).toString(),
      family: Family.product,
      packageRowsAssociation: packageRowIdentifier,
      responsibleToReport: data.responsibleToReport,
      internalReference: data.productNumber || "",
    };

    const rowPayload = createRowPayload({ ...data, reportId: currentReportId });

    await createProductAndReportRow(
      createPackageRowPayload,
      createProductPayload,
      rowPayload,
      ecomodClasses,
      supClasses,
    );

    handleCloseView();

    if (!reportId && currentReportId) {
      navigate(`/report/${currentReportId}`);
    }
  };

  const isLoading =
    isLoadingReport || isLoadingEcomodClasses || isLoadingSupClasses;

  return (
    <LoadingContainer
      status={isLoading ? HttpRequestStatus.LOADING : HttpRequestStatus.SUCCESS}
    >
      {showImportExcelDialog && (year || reportData?.year) && (
        <FileUploadDialog
          show={showImportExcelDialog}
          onHide={() => setShowImportExcelDialog(false)}
          onAccept={() => {
            console.log("Accepted");
          }}
          reportId={reportId ? (reportId as number) : undefined}
          year={year || (reportData?.year as number)}
          quarter={quarter}
          month={month}
        />
      )}
      {showCreateProductView && (
        <CreateProductView
          show={showCreateProductView}
          onHide={handleCloseView}
          ean={searchEan}
          handleCreateNewRow={handleCreateNewRow}
          handleCreateNewRowFromModel={handleCreateNewRowFromModel}
          ecomodClasses={ecomodClasses ?? []}
          supClasses={supClasses ?? []}
          synkkaData={synkkaData}
        />
      )}
      {showCreateMaterialView && (
        <CreateMaterialView
          show={showCreateMaterialView}
          onHide={() => setShowCreateMaterialView(false)}
          ecomodClasses={ecomodClasses ?? []}
          supClasses={supClasses ?? []}
          reportId={reportId as number}
        />
      )}

      {showEditReportRowView && (
        <EditReportRowView
          ean={showEditReportRowView.ean}
          show={!!showEditReportRowView}
          onHide={() => setShowEditReportRowView(null)}
          reportRow={showEditReportRowView}
          reportId={reportId as number}
          ecomodClasses={ecomodClasses ?? []}
          supClasses={supClasses ?? []}
        />
      )}

      {!showCreateProductView &&
        !showEditReportRowView &&
        !showCreateMaterialView && (
          <EANSearch
            onAddProduct={handleSearchProduct}
            loading={eanSearchLoading}
            setShowImportExcelDialog={setShowImportExcelDialog}
            setShowCreateMaterialView={setShowCreateMaterialView}
          />
        )}

      {!showCreateProductView &&
        !showCreateMaterialView &&
        reportData &&
        reportId &&
        !showEditReportRowView && (
          <ReportListItem
            reportRows={reportData?.rows}
            onRemove={handleRemoveReport}
            updateReportRow={updateReportRowData}
            reportId={reportId}
            reportYear={reportData?.year}
            isLoading={isLoadingReport || isUpdatingReportRow}
            ecomodClasses={ecomodClasses}
            supClasses={supClasses}
          />
        )}
    </LoadingContainer>
  );
};

export default ReportForm;
